import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

const base =
  'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition ' +
  'disabled:cursor-not-allowed disabled:opacity-50'

const variants: Record<Variant, string> = {
  primary: 'bg-forest-700 text-white hover:bg-forest-800 active:bg-forest-900 shadow-sm',
  secondary:
    'border border-[color:var(--surface-border)] bg-[color:var(--surface-raised)] ' +
    'text-[color:var(--text-strong)] hover:bg-[color:var(--surface-sunken)]',
  ghost: 'text-[color:var(--text-strong)] hover:bg-[color:var(--surface-sunken)]',
  danger: 'bg-red-700 text-white hover:bg-red-800',
}

const sizes: Record<Size, string> = {
  // Comfortably past the 44px touch target on the two larger sizes, because Scouts tap
  // these on a phone with gloves on in February.
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
}

function classes(variant: Variant, size: Size, className?: string) {
  return [base, variants[variant], sizes[size], className].filter(Boolean).join(' ')
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
}

export function Button({ variant = 'primary', size = 'md', className, ...props }: ButtonProps) {
  return <button className={classes(variant, size, className)} {...props} />
}

interface ButtonLinkProps {
  to: string
  variant?: Variant
  size?: Size
  className?: string
  children: ReactNode
}

/** Same visuals as <Button>, but renders an anchor so it behaves like a link. */
export function ButtonLink({ to, variant = 'primary', size = 'md', className, children }: ButtonLinkProps) {
  return (
    <Link to={to} className={classes(variant, size, className)}>
      {children}
    </Link>
  )
}
